import React, { ReactNode } from 'react';
import clsx from 'clsx';

// components
import { AvatarProps } from '@/components/elements/Avatar';
import Typography from '@/components/elements/Typography';
import Link from 'next/link';
import ConnectWalletRedirectButton from '@/components/modules/ConnectWalletRedirectButton';

// utils
import { ellipseAddress, thousandSeparator } from '@/utils';
import Button from '@/components/elements/Button';

// icons
import { useAuth } from '@/providers/AuthProvider';
import UserAvatar from '../UserAvatar';

type Classes = {
  root: string;
  avatar: string;
  avatarContainer: string;
  contentContainer: string;
  address: string;
  balance: string;
};

export type AccountProps = {
  classes?: Partial<Classes>;
  children?: ReactNode;
} & Pick<AvatarProps, 'src' | 'alt'>;

/**
 * @component Account
 */
function Account({ src, alt, classes }: AccountProps) {
  const { active, account, logout } = useAuth();

  // #region Classes
  const rootClasses = clsx(`flex flex-col items-center`, classes?.root);
  const avatarContainerClasses = clsx(
    `flex items-center justify-center`,
    classes?.avatarContainer,
  );
  const avatarClasses = clsx(classes?.avatar);
  const contentContainerClasses = clsx(
    `flex flex-col ml-3`,
    classes?.contentContainer,
  );
  const addressClasses = clsx(
    `!text-[12px] mb-1 !font-bold !leading-[15px]`,
    classes?.address,
  );
  const balanceClasses = clsx(
    `!text-[10px] !font-bold !leading-[12px] text-gray-500`,
    classes?.balance,
  );

  if (!active)
    return (
      <div className="flex flex-col items-center">
        <ConnectWalletRedirectButton className="mb-2" />

        <Link href="/connect/?type=email">
          <Button color="default" variant="text">
            <Typography variant="caption" className="text-gray-600">
              or login via email
            </Typography>
          </Button>
        </Link>
      </div>
    );
  // #endregion
  return (
    <div className={rootClasses}>
      <div className="flex items-center mb-4">
        <div className={avatarContainerClasses}>
          <UserAvatar
            walletAddress={account as string}
            alt={alt}
            src={src}
            size={32}
            className={avatarClasses}
          />
        </div>

        <div className={contentContainerClasses}>
          <Typography className={addressClasses}>
            {account ? ellipseAddress(account) : `Loading...`}
          </Typography>
          <Typography className={balanceClasses} variant="footer">
            ${thousandSeparator(156_423)} in exchange
          </Typography>
        </div>
      </div>

      <Button onClick={logout} size="small" variant="outlined" color="primary">
        Disconnect Wallet
      </Button>
    </div>
  );
}

export default Account;
